/**
 * YouTube utility functions for handling YouTube URLs and extracting video IDs
 */
export class YouTubeUtils {
    /**
     * Extracts YouTube video ID from various YouTube URL formats
     * @param url YouTube URL
     * @returns Video ID if found, null otherwise
     */
    static extractVideoId(url: string): string | null {
        if (!url) return null;

        // Remove any whitespace
        url = url.trim();

        // YouTube URL patterns
        const patterns = [
            // Standard watch URL: https://www.youtube.com/watch?v=VIDEO_ID
            /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
            // Short URL: https://youtu.be/VIDEO_ID
            /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
            // Embed URL: https://www.youtube.com/embed/VIDEO_ID
            /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
            // Playlist URL with video: https://www.youtube.com/watch?v=VIDEO_ID&list=...
            /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }

        return null;
    }

    /**
     * Validates if a YouTube URL is valid
     * @param url YouTube URL
     * @returns true if valid, false otherwise
     */
    static isValidYouTubeUrl(url: string): boolean {
        return this.extractVideoId(url) !== null;
    }

    /**
     * Converts a YouTube URL to an embed URL
     * @param url YouTube URL
     * @returns Embed URL if valid, null otherwise
     */
    static getEmbedUrl(url: string): string | null {
        const videoId = this.extractVideoId(url);
        if (!videoId) return null;

        // Use privacy-enhanced mode (youtube-nocookie.com) to reduce cookie issues
        // Add parameters to minimize cookie usage and improve privacy
        return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`;
    }

    /**
     * Gets the thumbnail URL for a YouTube video
     * @param url YouTube URL
     * @param quality Thumbnail quality ('default', 'medium', 'high', 'standard', 'maxres')
     * @returns Thumbnail URL if valid, null otherwise
     */
    static getThumbnailUrl(
        url: string,
        quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'medium'
    ): string | null {
        const videoId = this.extractVideoId(url);
        if (!videoId) return null;

        const qualityMap = {
            default: 'default',
            medium: 'mqdefault',
            high: 'hqdefault',
            standard: 'sddefault',
            maxres: 'maxresdefault'
        };

        return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
    }

    /**
     * Formats a YouTube URL to a standard watch URL
     * @param url YouTube URL
     * @returns Standard watch URL if valid, null otherwise
     */
    static getStandardUrl(url: string): string | null {
        const videoId = this.extractVideoId(url);
        if (!videoId) return null;

        return `https://www.youtube.com/watch?v=${videoId}`;
    }
}
